/**
 * Pure routing helpers for MRT (MQTT) messages.
 *
 * Behavior-preserving extraction from `MrtContext.tsx`'s `onMessage`
 * switch. These functions return the ordered list of Redux actions that
 * the live `onMessage` should dispatch for a given parsed MQTT payload.
 *
 * Side effects (refetchUsers, RTK Query tag invalidation, presence,
 * setLastMessageJson, console.log) stay in `MrtContext.tsx`. Only the
 * action-creation logic for `SstReservationUpdate` lives here so it can
 * be contract-tested without mounting the provider.
 *
 * Why the V1/V2 split is the bug-prevention surface:
 *
 *  - V1 reservation payload shape: top-level `_id` (no top-level `id`),
 *    nested `door` carrying `door_id` and `locker_id`. Status enum
 *    includes `CLOSE` (not `CLOSED`).
 *  - V2 reservation payload shape: top-level string `id` (SST V2 UUID),
 *    `door_id` as a sibling. Status enum uses `CLOSED`/`CANCELLED`/
 *    `DELETED`.
 *
 * The legacy V1 dispatch is gated only by `reservation.door.locker_id`.
 * The V2 dispatch is gated by `typeof reservation.id === "string"`.
 * Both can fire for a single payload when both guards pass; that
 * mirrors the live handler exactly.
 */
import type { AnyAction } from "@reduxjs/toolkit"
import { SnapshotSchema } from "appsShared/inAppMessages"
import { InAppMessagesTopic } from "shared/inAppMessagesData"
import { LockerThingTypeMrn } from "shared/lockerData"
import { SharedAccessPatternOsEnum } from "shared/sharedAccessPatternsData"
import { inAppMessagesActions } from "../features/inAppMessages/inAppMessagesSlice"
import { sstThingsActions } from "../features/partners/sstThingsSlice"
import { thingsApi } from "../features/things/thingsApi"
import { thingsActions } from "../features/things/thingsSlice"

/**
 * The in-app messages snapshot (DEV-443) is routed by TOPIC: it has no `type` field, so the
 * `type` switch in `MrtContext.tsx` would drop it. The zod parse runs before any dispatch;
 * an invalid payload, or one for another app, yields no action and the last valid snapshot stays.
 * Returns null when the topic is not the snapshot topic, so the caller falls through to `type`.
 */
export function routeInAppMessagesSnapshot(
  topic: string,
  payload: unknown,
): AnyAction[] | null {
  if (topic !== InAppMessagesTopic) return null
  const parsed = SnapshotSchema.safeParse(payload)
  if (!parsed.success || parsed.data.appCode !== "sl2") {
    console.warn("In-app messages snapshot ignored: invalid payload")
    return []
  }
  return [inAppMessagesActions.snapshotReceived(parsed.data)]
}

/**
 * Return the ordered list of Redux actions to dispatch for a single
 * `SstReservationUpdate` MRT message. Mirrors `MrtContext.tsx`'s
 * switch case verbatim so the runtime semantics stay identical.
 */
export function routeSstReservationUpdate(reservation: any): AnyAction[] {
  const actions: AnyAction[] = []

  if (reservation?.door?.locker_id) {
    actions.push(
      sstThingsActions.upsertOneDeep({
        _id: reservation?._id,
        sstType: "SstReservation",
        ...reservation,
      }),
    )
  }

  // V2 reservations carry a top-level string `id` (SST V2 UUID). V1
  // payloads use `_id` + nested `door` (no top-level `id`), so they
  // fail this discriminator and skip the V2 path. The reducer is also
  // a no-op when no matching V2 locker is in state, so this dispatch
  // is safe either way.
  if (typeof reservation?.id === "string") {
    actions.push(thingsActions.applyV2ReservationUpdate(reservation))
  }

  return actions
}

/**
 * `ThingRefetch` (LOCKER-509) is the identity-only dsync notice the backend
 * sends when a thing is too large for one IoT message. Returns the existing
 * `getThings` request that refetches that locker, so the response goes through
 * the normal `getThings` merge (LOCKER-321 reconcile included). Returns null
 * when the notice is not for a locker or lacks its identity.
 */
export function routeThingRefetch(data: any) {
  if (data?.thingTypeMrn !== LockerThingTypeMrn) return null
  if (typeof data?.id !== "string" || typeof data?.tenantId !== "string") {
    return null
  }
  return thingsApi.endpoints.getThings.initiate(
    {
      params: {
        "ap.name": SharedAccessPatternOsEnum.GetLockerById,
        "ap.tenantId": data.tenantId,
        "ap.id": data.id,
        tenantId: data.tenantId,
      },
    },
    { subscribe: false, forceRefetch: true },
  )
}
